import React, { useState } from 'react';
import { toast } from 'sonner';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { useAdmin } from '../contexts/AdminContext';
import { usePageTitle } from '../hooks/usePageTitle';
import useCommunityGroups from '../hooks/useCommunityGroups';
import {
  MessageCircle,
  Headphones,
  Shield,
  Heart,
  Sparkles,
  ArrowRight,
  Users,
  Zap,
  BadgeCheck,
  Pencil,
  Check,
  X,
} from 'lucide-react';

const WHATSAPP_SUPPORT_LINK = 'https://wa.me/5577988744698?text=Olá!%20Preciso%20de%20suporte%20com%20o%20Dark%20Planner.';

const GRAD = 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)';

/**
 * Comunidade & Suporte — dois painéis premium (padrão DESIGN_SYSTEM.md §6).
 *
 * Regras de acesso:
 *  - SUPORTE: aberto para TODO usuário logado (free incluso).
 *  - COMUNIDADE (3 grupos de WhatsApp): exclusiva de plano PAGO ativo
 *    (estado_efetivo === 'PREMIUM': basic, lite, pro, plus). Quem não paga NÃO
 *    vê o painel, e os links nem chegam ao navegador: o backend só os devolve
 *    para plano pago ativo (GET /api/community/whatsapp-groups).
 */
const ComunidadePage = ({ onLogout }) => {
  usePageTitle('Comunidade & Suporte');
  const { sidebarWidth, isMobile } = useSidebar();
  const { subscriptionData, isSuperAdmin } = useAdmin();
  // Assinante pago (qualquer plano MENOS free) ou super admin.
  const isPaying = !!(subscriptionData && (subscriptionData.is_super_admin || subscriptionData.estado_efetivo === 'PREMIUM'));

  // Grupos vêm do backend (só para quem paga); super admin edita inline aqui
  const { groups, saveGroup } = useCommunityGroups(isPaying);
  const visibleGroups = groups.filter((g) => g.url || isSuperAdmin);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingLink, setSavingLink] = useState(false);

  const handleSaveLink = async () => {
    const value = editValue.trim();
    if (!editingKey || !value.startsWith('https://') || savingLink) return;
    setSavingLink(true);
    try {
      await saveGroup(editingKey, value);
      toast.success('Link do grupo atualizado');
      setEditingKey(null);
    } catch {
      toast.error('Erro ao salvar o link');
    } finally {
      setSavingLink(false);
    }
  };

  // ── blocos visuais compartilhados ──
  const iconBadge = (Icon) => (
    <div style={{
      width: '46px', height: '46px', borderRadius: '12px', flexShrink: 0,
      background: GRAD, boxShadow: '0 4px 14px rgba(13,174,255,0.28)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={22} color="#fff" strokeWidth={2.2} />
    </div>
  );

  const pill = (text) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase',
      color: 'var(--dp-blue-500)',
      background: 'rgba(13,174,255,0.08)',
      border: '1px solid rgba(13,174,255,0.22)',
      padding: '4px 11px', borderRadius: 'var(--dp-radius-full, 9999px)', whiteSpace: 'nowrap',
      fontFamily: 'var(--dp-font)',
    }}>
      <Sparkles size={11} />
      {text}
    </span>
  );

  const featureRow = (Icon, text) => (
    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{
        width: '26px', height: '26px', borderRadius: '8px', flexShrink: 0,
        background: 'rgba(13,174,255,0.08)', border: '1px solid rgba(13,174,255,0.18)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={13} style={{ color: 'var(--dp-blue-500)' }} />
      </div>
      <span style={{ fontSize: '12.5px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', lineHeight: 1.5 }}>
        {text}
      </span>
    </div>
  );

  const panelStyle = (delay) => ({
    position: 'relative', overflow: 'hidden',
    background: '#141414', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '20px', padding: isMobile ? '22px' : '28px',
    display: 'flex', flexDirection: 'column', gap: '18px',
    transition: 'border-color .2s cubic-bezier(.4,0,.2,1), transform .2s cubic-bezier(.4,0,.2,1)',
    animation: `dp-fadeInUp .5s ease ${delay}s both`,
  });

  const panelHover = {
    onMouseEnter: (e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.15)'; e.currentTarget.style.transform = 'translateY(-2px)'; },
    onMouseLeave: (e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; },
  };

  const glow = (
    <div style={{
      position: 'absolute', top: '-150px', right: '-110px', width: '420px', height: '420px',
      borderRadius: '50%', background: 'radial-gradient(circle, rgba(13,174,255,0.07), transparent 65%)',
      pointerEvents: 'none',
    }} />
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />

      <div style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        flex: 1, minWidth: 0,
        transition: 'margin-left 0.3s ease',
        display: 'flex', flexDirection: 'column',
      }}>
        <Header />

        <main className="dp-main-content" style={{ marginTop: '60px', flex: 1, animation: 'dp-pageFadeIn 0.35s ease' }}>
          <PageHeader
            icon={<MessageCircle size={20} style={{ color: 'var(--dp-blue-500)' }} />}
            title="Comunidade & Suporte"
            description="Conecte-se com outros creators ou fale direto com a nossa equipe"
          />

          {/* ═══ PAINÉIS: Comunidade (só plano pago) · Suporte (todos) ═══ */}
          <div style={{
            display: 'grid', gap: '16px', marginBottom: '24px',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            alignItems: 'stretch',
          }}>
            {/* ── COMUNIDADE — só aparece para plano pago ativo ── */}
            {isPaying && (
            <section className="dp-top-accent" style={panelStyle(0.05)} {...panelHover}>
              {glow}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                {iconBadge(Users)}
                {pill('Comunidade ativa')}
              </div>

              <div style={{ position: 'relative' }}>
                <h2 style={{ margin: '0 0 8px', fontSize: '21px', fontWeight: 700, letterSpacing: '-0.4px', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                  Comunidade DarkPlanner
                </h2>
                <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--dp-text-secondary)', lineHeight: 1.65, fontFamily: 'var(--dp-font)' }}>
                  Os grupos oficiais no WhatsApp onde creators que operam canais trocam estratégia todos os dias.
                  Novidades da plataforma chegam lá primeiro.
                </p>
              </div>

              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {featureRow(Users, 'Network com creators que já operam canais no dia a dia')}
                {featureRow(Zap, 'Updates e novidades da plataforma em primeira mão')}
                {featureRow(Heart, 'Troca de experiências, nichos e resultados reais')}
              </div>

              <div style={{ position: 'relative', marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {visibleGroups.map((g) => (
                  editingKey === g.key ? (
                    // Edição do link — só super admin chega aqui
                    <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="url"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveLink();
                          if (e.key === 'Escape') setEditingKey(null);
                        }}
                        autoFocus
                        placeholder={`Link do Grupo ${g.number} (https://chat.whatsapp.com/...)`}
                        style={{
                          flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: '8px',
                          background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-medium)',
                          color: 'var(--dp-text-primary)', fontSize: '12px', fontFamily: 'var(--dp-font)',
                          outline: 'none',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-medium)'; }}
                      />
                      <button
                        type="button"
                        onClick={handleSaveLink}
                        disabled={savingLink || !editValue.trim().startsWith('https://')}
                        title="Salvar link"
                        style={{
                          width: '30px', height: '30px', borderRadius: '8px', border: 'none', padding: 0,
                          background: GRAD, color: '#fff', display: 'inline-flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                          opacity: savingLink ? 0.6 : 1, flexShrink: 0,
                        }}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingKey(null)}
                        title="Cancelar"
                        style={{
                          width: '30px', height: '30px', borderRadius: '8px', padding: 0,
                          border: '1px solid var(--dp-border-medium)', background: 'transparent',
                          color: 'var(--dp-text-tertiary)', display: 'inline-flex',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        className="dp-btn-cta"
                        style={{ flex: 1, minWidth: 0, opacity: g.url ? 1 : 0.5 }}
                        disabled={!g.url}
                        onClick={() => window.open(g.url, '_blank', 'noopener,noreferrer')}
                      >
                        <MessageCircle size={16} />
                        {g.url ? `Entrar no Grupo ${g.number}` : `Grupo ${g.number} · sem link`}
                        <ArrowRight size={16} />
                      </button>
                      {isSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => { setEditValue(g.url || ''); setEditingKey(g.key); }}
                          title={`Editar link do Grupo ${g.number}`}
                          style={{
                            width: '34px', height: '34px', borderRadius: '8px', padding: 0, flexShrink: 0,
                            border: '1px solid var(--dp-border-medium)', background: 'transparent',
                            color: 'var(--dp-text-tertiary)', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#0DAEFF'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.3)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                    </div>
                  )
                ))}
              </div>
            </section>
            )}

            {/* ── SUPORTE — aberto para TODOS os usuários ── */}
            <section className="dp-top-accent" style={panelStyle(0.12)} {...panelHover}>
              {glow}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                {iconBadge(Headphones)}
                {pill('Resposta rápida')}
              </div>

              <div style={{ position: 'relative' }}>
                <h2 style={{ margin: '0 0 8px', fontSize: '21px', fontWeight: 700, letterSpacing: '-0.4px', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)' }}>
                  Suporte Técnico
                </h2>
                <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--dp-text-secondary)', lineHeight: 1.65, fontFamily: 'var(--dp-font)' }}>
                  Travou, deu erro ou ficou com dúvida? Fale direto com a equipe oficial do DarkPlanner
                  no WhatsApp, disponível para todos os usuários.
                </p>
              </div>

              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {featureRow(BadgeCheck, 'Atendimento oficial da equipe DarkPlanner')}
                {featureRow(Zap, 'Resolução de problemas técnicos e de conta')}
                {featureRow(Headphones, 'Ajuda com qualquer funcionalidade da plataforma')}
              </div>

              <div style={{ position: 'relative', marginTop: 'auto' }}>
                <button
                  className="dp-btn-cta"
                  style={{ width: '100%' }}
                  onClick={() => window.open(WHATSAPP_SUPPORT_LINK, '_blank')}
                >
                  <Headphones size={16} /> Falar com Suporte <ArrowRight size={16} />
                </button>
              </div>
            </section>
          </div>

          {/* ═══ Faixa de confiança ═══ */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px',
            animation: 'dp-fadeInUp .5s ease .2s both',
          }}>
            {[
              { icon: Heart, text: 'Ambiente de respeito e colaboração' },
              { icon: Shield, text: 'Canais oficiais gerenciados pela equipe' },
              { icon: Sparkles, text: 'Conteúdo relevante, sem spam' },
            ].map((item) => {
              const InfoIcon = item.icon;
              return (
                <span key={item.text} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '7px',
                  fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)',
                  padding: '8px 14px', borderRadius: 'var(--dp-radius-full, 9999px)',
                }}>
                  <InfoIcon size={13} style={{ color: 'var(--dp-blue-500)', opacity: 0.8 }} />
                  {item.text}
                </span>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ComunidadePage;
